import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import { type APIPaginatedResponse, EndpointService } from "@/types/api/api";
import type { AxiosResponse } from "axios";

export enum NotificationCategory {
  ALL = "all",
  Unread = "unread",
  Invoices = "invoices",
  Statements = "statements",
  ThirdPartyAccounts = "third party accounts",
  OrganisationInvites = "organisation invites",
}

export enum NotificationType {
  InvoiceCreated = "dines.notifications.invoice.created",
  StatementCreated = "dines.notifications.statement.created",
  ThirdPartyAccountLinked = "dines.notifications.organisations.third_party_account_linked",
  OrganisationInviteAccepted = "dines.notifications.organisations.invite_accepted",
}

export interface IListNotificationsParams {
  search?: string;
  page: number;
  page_size: number;
  order_by: string;
  category: NotificationCategory;
  start_date: string;
  end_date: string;
  is_read?: boolean;
  is_enabled?: boolean;
}

export interface INotification {
  id: number;
  created_at: string;
  updated_at: string;
  employee_id: number;
  title: string;
  description: string;
  notification: NotificationType;
  priority: number;
  notification_metadata: {
    object_id: number;
    object_type: string;
  };
  is_read: boolean;
}

export interface IListNotificationsResponse
  extends APIPaginatedResponse<INotification> { }

export interface IReadNotificationCount {
  count: number;
}

export interface IReadNotificationParams {
  notification_id: number;
}

export interface IReadNotificationResponse {
  id: number;
  created_at: string;
  updated_at: string;
  notification_id: number;
  restaurant_id: number;
  employee_id: number;
}

export interface IReadAllNotificationsResponse {
  status: "error" | "success";
  message: string;
}

export interface INotificationsService {
  list: (
    params: IListNotificationsParams,
  ) => UseQueryResult<IListNotificationsResponse, Error>;
  notificationCount: () => UseQueryResult<IReadNotificationCount, Error>;
  read: ({
    listParams,
  }: {
    listParams: Record<string, unknown>;
  }) => UseMutationResult<
    IReadNotificationResponse,
    Error,
    IReadNotificationParams
  >;
  readAll: ({
    listParams,
  }: {
    listParams: Record<string, unknown>;
  }) => UseMutationResult<IReadAllNotificationsResponse, Error>;
}

export class NotificationsService implements INotificationsService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  private processListParams(params: Record<string, unknown>): Record<string, unknown> {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value != null),
    );
    const { is_enabled: _isEnabled, ...restParams } = filtered;

    if (params.category === NotificationCategory.Unread) {
      restParams.is_read = false;
      restParams.category = undefined;
    }

    if (params.category === NotificationCategory.ALL) {
      restParams.category = undefined;
    }

    return Object.fromEntries(
      Object.entries(restParams).filter(([, value]) => value != null),
    );
  }

  list(
    params: IListNotificationsParams,
  ): UseQueryResult<IListNotificationsResponse, Error> {
    const { is_enabled: isEnabled = false } = params;
    const restParams = this.processListParams(params as unknown as Record<string, unknown>);

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["list", "notifications"],
        venueID: null,
        params: restParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IListNotificationsResponse>
        >("/notifications/", {
          params: restParams,
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return {
          results: data.results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
      enabled: isEnabled !== undefined ? isEnabled : true,
    });
  }

  notificationCount(): UseQueryResult<IReadNotificationCount, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["notification", "count"],
        venueID: null,
        params: {},
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IReadNotificationCount>
        >("/notifications/unread-count/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
    });
  }

  read({
    listParams,
  }: {
    listParams: Record<string, unknown>;
  }): UseMutationResult<
    IReadNotificationResponse,
    Error,
    IReadNotificationParams
  > {
    const queryClient = useQueryClient();
    const processedListParams = this.processListParams(listParams);
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["read", "notification"],
        venueID: null,
        params: {},
      }),
      mutationFn: async (params: IReadNotificationParams) => {
        const { data } = await this.api.post<
          AxiosResponse<IReadNotificationResponse>
        >("/notifications/read/", {
          params,
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
      onMutate: async (params: IReadNotificationParams) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
        });

        const previousNotifications = await queryClient.getQueryData(
          buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
        );

        await queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
          (old: IListNotificationsResponse) => {
            return {
              ...old,
              results: old.results.map((result) =>
                result.id === params.notification_id
                  ? { ...result, is_read: true }
                  : result,
              ),
            };
          },
        );

        return { previousNotifications };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
        });
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["notification", "count"],
            venueID: null,
            params: {},
          }),
        });
      },
      onError: async (error, variables, context) => {
        await queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
          context,
        );
      },
    });
  }

  readAll({
    listParams,
  }: {
    listParams: Record<string, unknown>;
  }): UseMutationResult<IReadAllNotificationsResponse, Error> {
    const queryClient = useQueryClient();
    const processedListParams = this.processListParams(listParams);
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["read", "all", "notifications"],
        venueID: null,
        params: {},
      }),
      mutationFn: async () => {
        const { data } = await this.api.post<
          AxiosResponse<IReadAllNotificationsResponse>
        >("/notifications/read/all", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
      onMutate: async () => {
        const countQueryKey = buildQueryKey({
          queryKey: ["notification", "count"],
          venueID: null,
          params: {},
        });
        const listQueryKey = buildQueryKey({
          queryKey: ["list", "notifications"],
          venueID: null,
          params: processedListParams,
        });

        await queryClient.cancelQueries({ queryKey: countQueryKey });
        await queryClient.cancelQueries({ queryKey: listQueryKey });

        const previousNotificationCount = queryClient.getQueryData(countQueryKey);
        const previousNotifications = queryClient.getQueryData(listQueryKey);

        queryClient.setQueryData(
          countQueryKey,
          (old: IReadNotificationCount) => ({
            ...old,
            count: 0,
          }),
        );

        queryClient.setQueryData(
          listQueryKey,
          (old: IListNotificationsResponse) => {
            if (!old) return old;
            return {
              ...old,
              results: old.results.map((result) => ({
                ...result,
                is_read: true,
              })),
            };
          },
        );

        return { previousNotificationCount, previousNotifications };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["notification", "count"],
            venueID: null,
            params: {},
          }),
        });
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
        });
      },
      onError: async (error, variables, context) => {
        queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["notification", "count"],
            venueID: null,
            params: {},
          }),
          context?.previousNotificationCount,
        );
        queryClient.setQueryData(
          buildQueryKey({
            queryKey: ["list", "notifications"],
            venueID: null,
            params: processedListParams,
          }),
          context?.previousNotifications,
        );
      },
    });
  }
}
